import { apiFetch } from '@/lib/api'
import { env } from '@/lib/env'
import type { RealtimeService } from '@/services/contracts'
import type { RealtimeEvent } from '@/types/models'

interface StreamTicketDto {
  ticket: string
  expires_in: number
}

const EVENT_NAME = 'data-changed'
const RECONNECT_MIN_MS = 1_000
const RECONNECT_MAX_MS = 30_000

/**
 * `EventSource` özel header taşıyamadığı için access token query'de taşınmaz;
 * bunun yerine `POST /realtime/ticket` ile alınan tek kullanımlık bilet kullanılır
 * (sözleşme §6, onaylanan sapma — bkz. docs/OPEN-QUESTIONS.md #8).
 */
export const httpRealtimeService: RealtimeService = {
  connect(handlers) {
    let cancelled = false
    let source: EventSource | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let reconnectDelay = RECONNECT_MIN_MS

    function teardownSource() {
      source?.close()
      source = null
    }

    function scheduleReconnect() {
      if (cancelled) return
      handlers.onStatusChange('reconnecting')
      reconnectTimer = setTimeout(() => {
        reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS)
        void open()
      }, reconnectDelay)
    }

    async function open() {
      if (cancelled) return
      handlers.onStatusChange('connecting')
      try {
        const { ticket } = await apiFetch<StreamTicketDto>('/realtime/ticket', { method: 'POST' })
        if (cancelled) return

        const url = `${env.apiBaseUrl}/realtime/stream?ticket=${encodeURIComponent(ticket)}`
        const es = new EventSource(url)
        source = es

        es.onopen = () => {
          reconnectDelay = RECONNECT_MIN_MS
          handlers.onStatusChange('open')
        }

        es.addEventListener(EVENT_NAME, (message) => {
          try {
            handlers.onEvent(JSON.parse((message as MessageEvent<string>).data) as RealtimeEvent)
          } catch {
            // Bozuk gövde; bağlantı canlı kalır, yalnızca bu olay yok sayılır.
          }
        })

        es.onerror = () => {
          teardownSource()
          scheduleReconnect()
        }
      } catch {
        scheduleReconnect()
      }
    }

    void open()

    return () => {
      cancelled = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      teardownSource()
      handlers.onStatusChange('closed')
    }
  },
}
